import { Endpoint } from "@rest-hooks/rest";
import { api_url, auth_headers } from "src/context";
import { Maybe } from "src/utils";

export type UwConfigType = "ready-plans" | "costs-and-metrics" | "amenity-adjustments";

export type UwConfigValue = string | number | boolean | number[] | null;

export type FieldAttribution = {
  updated_at: string;
  // null for values from the original config files
  updated_by: Maybe<string>;
};

export type UwConfigRow = {
  id: number;
  updated_at: string;
  updated_by: Maybe<string>;
  field_updates: Record<string, FieldAttribution>;
  [field: string]: any;
};

export type UwConfigRows = {
  config_type: UwConfigType;
  version: number;
  rows: UwConfigRow[];
};

export type UwConfigChange = {
  action: "create" | "update" | "delete";
  row_id: Maybe<number>;
  row_label: string;
  field: Maybe<string>;
  old_value: any;
  new_value: any;
};

export type UwConfigChangeSet = {
  config_type: UwConfigType;
  version: number;
  dry_run: boolean;
  batch_id: Maybe<string>;
  summary: { created: number; updated: number; deleted: number };
  changes: UwConfigChange[];
};

export type UwConfigExport = {
  config_type: UwConfigType;
  /** The version the config was exported at. Imports send it back unchanged to detect concurrent changes. */
  exported_version: number;
  config: any;
};

export type UwConfigHistoryEntry = UwConfigChange & {
  id: number;
  batch_id: string;
  config_type: UwConfigType;
  user: Maybe<string>;
  created_at: string;
};

/** Known values for a config's fields; which keys are present depends on the config type */
export type UwConfigOptions = {
  metro?: string[];
  property_type?: string[];
  sub_ready_plan_id?: string[];
  section?: string[];
  variable_by_section?: Record<string, string[]>;
  plan_level_sections?: string[];
  ready_plan_id_by_metro?: Record<string, string[]>;
  adjustment_name_by_key?: Record<string, string>;
  trainable_metro?: string[];
};

const path = (configType: UwConfigType, rest = "") => `/v1/uw-config/${configType}${rest}`;

async function request(url: string, method = "GET", body?: unknown) {
  const headers = { "content-type": "application/json", ...(await auth_headers()) };
  const res = await fetch(api_url(url), {
    headers,
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  // errors come back as chalice {Code, Message} bodies, which useController surfaces as alerts
  return await res.json();
}

/** All rows of a config, with who last changed each row and field */
export const UwConfigRowsEndpoint = new Endpoint(
  async ({ configType }: { configType: UwConfigType }): Promise<UwConfigRows> => request(path(configType)),
  { key: ({ configType }) => path(configType), name: "UwConfigRowsEndpoint" },
);

/** Known values for a config's fields, e.g. for dropdowns */
export const UwConfigOptionsEndpoint = new Endpoint(
  async ({ configType }: { configType: UwConfigType }): Promise<{ options: UwConfigOptions }> =>
    request(path(configType, "/options")),
  { key: ({ configType }) => path(configType, "/options"), name: "UwConfigOptionsEndpoint" },
);

/** The whole config as JSON in the shape of the original config files, plus the version it was exported at */
export const UwConfigExportEndpoint = new Endpoint(
  async ({ configType }: { configType: UwConfigType }): Promise<UwConfigExport> => request(path(configType, "/export")),
  { key: ({ configType }) => path(configType, "/export"), name: "UwConfigExportEndpoint" },
);

/** Replace a config with an (edited) export. With `dryRun` the changes are returned without being saved. */
export const UwConfigImportEndpoint = new Endpoint(
  async ({
    configType,
    document,
    dryRun,
  }: {
    configType: UwConfigType;
    document: UwConfigExport;
    dryRun: boolean;
  }): Promise<UwConfigChangeSet> => request(path(configType, `/import?dry_run=${dryRun}`), "POST", document),
  { sideEffect: true, name: "UwConfigImportEndpoint" },
);

export const UwConfigCreateRowEndpoint = new Endpoint(
  async ({
    configType,
    row,
  }: {
    configType: UwConfigType;
    row: Record<string, UwConfigValue>;
  }): Promise<UwConfigChangeSet> => request(path(configType, "/rows"), "POST", { row }),
  { sideEffect: true, name: "UwConfigCreateRowEndpoint" },
);

export const UwConfigUpdateRowEndpoint = new Endpoint(
  async ({
    configType,
    rowId,
    changes,
  }: {
    configType: UwConfigType;
    rowId: number;
    changes: Record<string, UwConfigValue>;
  }): Promise<UwConfigChangeSet> => request(path(configType, `/rows/${rowId}`), "PATCH", { changes }),
  { sideEffect: true, name: "UwConfigUpdateRowEndpoint" },
);

export const UwConfigDeleteRowEndpoint = new Endpoint(
  async ({ configType, rowId }: { configType: UwConfigType; rowId: number }): Promise<UwConfigChangeSet> =>
    request(path(configType, `/rows/${rowId}`), "DELETE"),
  { sideEffect: true, name: "UwConfigDeleteRowEndpoint" },
);

export const UW_CONFIG_HISTORY_PAGE_SIZE = 100;

/** Change log, newest first. Page with `beforeId` (the last `id` of the previous page). */
export const UwConfigHistoryEndpoint = new Endpoint(
  async ({ beforeId }: { beforeId?: number }): Promise<{ changes: UwConfigHistoryEntry[] }> =>
    request(`/v1/uw-config-history?limit=${UW_CONFIG_HISTORY_PAGE_SIZE}${beforeId ? `&before_id=${beforeId}` : ""}`),
  {
    key: ({ beforeId }) => `/v1/uw-config-history?before_id=${beforeId ?? ""}`,
    name: "UwConfigHistoryEndpoint",
  },
);

export type AmenityTrainingCoefficient = {
  adjustment_key: string;
  value: number;
  /** 95% confidence interval */
  ci_min: number;
  ci_max: number;
  clip_min: Maybe<number>;
  clip_max: Maybe<number>;
};

export type AmenityTrainingJob = {
  id: string;
  metro: string;
  /** first day of the month the models were trained as of */
  training_month: string;
  status: "running" | "succeeded" | "failed";
  requested_by: Maybe<string>;
  created_at: string;
  updated_at: string;
  error: Maybe<string>;
  result: Maybe<{
    metro: string;
    training_month: string;
    /** one model per property type: single_family is a metro's model for all property types */
    models: { property_type: string; mape: number; coefficients: AmenityTrainingCoefficient[] }[];
  }>;
};

/** Train a metro's amenity models as of the latest complete month (returns the metro's job if it's already training) */
export const UwConfigStartTrainingEndpoint = new Endpoint(
  async ({ metro }: { metro: string }): Promise<AmenityTrainingJob> =>
    request("/v1/uw-config/amenity-training", "POST", { metro }),
  { sideEffect: true, name: "UwConfigStartTrainingEndpoint" },
);

export const UwConfigTrainingJobEndpoint = new Endpoint(
  async ({ jobId }: { jobId: string }): Promise<AmenityTrainingJob> =>
    request(`/v1/uw-config/amenity-training/${jobId}`),
  { key: ({ jobId }) => `/v1/uw-config/amenity-training/${jobId}`, name: "UwConfigTrainingJobEndpoint" },
);

/** A metro's training jobs, newest first */
export const UwConfigTrainingJobsEndpoint = new Endpoint(
  async ({ metro }: { metro: string }): Promise<{ jobs: AmenityTrainingJob[] }> =>
    request(`/v1/uw-config/amenity-training?metro=${encodeURIComponent(metro)}&limit=1`),
  { key: ({ metro }) => `/v1/uw-config/amenity-training?metro=${metro}`, name: "UwConfigTrainingJobsEndpoint" },
);
